
/**
 * Wingman Spatial Telemetry Infrastructure
 * 
 * PURPOSE: Structured, low-noise observability for the spatial runtime.
 * Provides consistent formatting, log levels, throttling, and correlation tracking.
 */

export type LogLevel = 'DEBUG' | 'INFO' | 'WARN' | 'ERROR' | 'FORENSIC' | 'PERF';

export interface TelemetryEvent {
    subsystem: string;
    event: string;
    level: LogLevel;
    source?: string;
    correlationId?: string;
    payload?: any;
    timestamp?: number;
}

const DEFAULT_LEVEL: LogLevel = 'INFO';

// 🛡️ THROTTLE STORAGE
const THROTTLE_MAP = new Map<string, number>();

// 📈 PERF METRICS
const PERF_METRICS = {
    intentThroughput: 0,
    queueDepth: 0,
    lastDispatchTime: 0,
    dispatchLatencies: [] as number[],
    cameraUpdateCount: 0,
    renderTimings: [] as number[],
    eventRecursionCount: 0
};

let currentlyLogging = false;

/**
 * Shared Telemetry Utility
 */
export const telemetry = {
    /**
     * Log an event with structured formatting
     */
    log: (options: TelemetryEvent) => {
        if (currentlyLogging) {
            PERF_METRICS.eventRecursionCount++;
            return;
        }
        currentlyLogging = true;

        try {
            const {
                subsystem,
                event,
                level = DEFAULT_LEVEL,
                source = 'SYSTEM',
                correlationId,
                payload,
                timestamp = Date.now()
            } = options;

            const sub = (subsystem || 'UNKNOWN').toUpperCase();
            const ev = (event || 'UNKNOWN').toUpperCase();
            
            // 🛡️ Prefix Standardization: [LEVEL][SUBSYSTEM][EVENT]
            const prefix = `[${level}][${sub}][${ev}]`;
            
            // Ensure CID exists for forensic logs
            let effectiveCid = correlationId;
            if (!effectiveCid && level === 'FORENSIC') {
                effectiveCid = `cid_auto_${Date.now()}_${Math.random().toString(36).substr(2, 5)}`;
            }
            
            const cidStr = effectiveCid ? ` [CID:${effectiveCid}]` : '';
            const sourceStr = source ? ` [SRC:${source}]` : '';
            const timestampStr = ` [TS:${timestamp}]`;

            const message = `${prefix}${cidStr}${sourceStr}${timestampStr}`;

            switch (level) {
                case 'ERROR':
                    console.error(message, payload);
                    break;
                case 'WARN':
                    console.warn(message, payload);
                    break;
                case 'DEBUG':
                    // Only show debug logs if explicitly enabled
                    if (typeof window !== 'undefined' && (window as any).__WINGMAN_DEBUG__) {
                        console.debug(message, payload);
                    }
                    break;
                case 'PERF':
                    console.info(`%c${message}`, 'color: #10b981; font-weight: bold;', payload);
                    break;
                case 'FORENSIC':
                    console.log(`%c${message}`, 'color: #0ea5e9; font-weight: bold;', payload);
                    break;
                case 'INFO':
                default:
                    console.info(message, payload);
            }

            // 📡 Integration with external trace recorders
            if (typeof window !== 'undefined' && (window as any).WingmanTraceRecorder) {
                // Ensure we don't pass events that are already from the trace recorder to avoid loops
                if (source !== 'TRACE_STORE') {
                    (window as any).WingmanTraceRecorder.recordDecision(
                        `${subsystem}:${event}`,
                        correlationId || null,
                        { ...payload, level, source, timestamp }
                    );
                }
            }
        } finally {
            currentlyLogging = false;
        }
    },

    /**
     * Log with throttling to prevent console flooding
     */
    throttledLog: (key: string, options: TelemetryEvent, limitMs: number = 1000) => {
        const now = Date.now();
        const last = THROTTLE_MAP.get(key) || 0;

        if (now - last > limitMs) {
            telemetry.log(options);
            THROTTLE_MAP.set(key, now);
            return true;
        }
        return false;
    },

    /**
     * Attach correlation ID to a payload
     */
    attachCorrelation: (payload: any, correlationId?: string) => {
        return {
            ...payload,
            correlationId: correlationId || `cid_${Date.now()}_${Math.random().toString(36).substr(2, 5)}`
        };
    },

    /**
     * Get all performance metrics
     */
    getPerfMetrics: () => ({ ...PERF_METRICS }),

    /**
     * Track performance metrics
     */
    trackPerf: (metric: keyof typeof PERF_METRICS, value: any) => {
        if (typeof PERF_METRICS[metric] === 'number') {
            if (metric === 'queueDepth' || metric === 'eventRecursionCount') {
                (PERF_METRICS[metric] as number) = value;
            } else {
                (PERF_METRICS[metric] as number) += value;
            }
        } else if (Array.isArray(PERF_METRICS[metric])) {
            (PERF_METRICS[metric] as any[]).push(value);
            if ((PERF_METRICS[metric] as any[]).length > 100) (PERF_METRICS[metric] as any[]).shift();
        }

        // Periodically log perf report
        telemetry.throttledLog('PERF_REPORT', {
            subsystem: 'TELEMETRY',
            event: 'PERF_METRICS',
            level: 'PERF',
            payload: { ...PERF_METRICS }
        }, 10000);
    },

    // Standardized Shorthand Contract
    debug: (subsystem: string, event: string, payload?: any, cid?: string) => 
        telemetry.log({ level: 'DEBUG', subsystem, event, payload, correlationId: cid }),
    
    info: (subsystem: string, event: string, payload?: any, cid?: string) => 
        telemetry.log({ level: 'INFO', subsystem, event, payload, correlationId: cid }),
    
    warn: (subsystem: string, event: string, payload?: any, cid?: string) => 
        telemetry.log({ level: 'WARN', subsystem, event, payload, correlationId: cid }),
    
    error: (subsystem: string, event: string, payload?: any, cid?: string) => 
        telemetry.log({ level: 'ERROR', subsystem, event, payload, correlationId: cid }),

    forensic: (subsystem: string, event: string, payload?: any, cid?: string) => 
        telemetry.log({ level: 'FORENSIC', subsystem, event, payload, correlationId: cid }),
        
    perf: (subsystem: string, event: string, payload?: any, cid?: string) => 
        telemetry.log({ level: 'PERF', subsystem, event, payload, correlationId: cid }),
};

// Global Exposure
if (typeof window !== 'undefined') {
    (window as any).WingmanTelemetry = telemetry;
}
